import React, { useState, useEffect } from "react";
import { Button, Row, Col, Form, Alert } from "react-bootstrap";
import Draggable from "react-draggable";
import { Modal, ModalHeader, ModalBody, ModalFooter } from "reactstrap";
import ServiceCalls from '../utils/serviceCalls.js';
import "../styles/PopUps.css";
import { Loading } from '../components/Loading';
import TimedDisplay from '../components/TimedDisplay';
import RequestUtils from '../utils/requestUtils';

const ResubmitEarlyWarningPopup = ({ setIsOpen, request, currentUser, onRequestUpdate }) => {
	const [isLoading, setIsLoading] = useState(true);
	const [isBusiness, setIsBusiness] = useState(true);
	const [stateList, setStateList] = useState([]);
	const [bankState, setBankState] = useState("");
	//const [bankPostal, setBankPostal] = useState("");
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [submissionMessage, setSubmissionMessage] = useState("");
	const [submissionMessageType, setSubmissionMessageType] = useState("");
	const [isUpdateComplete, setIsUpdateComplete] = useState(false);
	const [validationErrors, setValidationErrors] = useState({});
	const [formData, setFormData] = useState({
		supplierName: "",
		firstName: "",
		middleName: "",
		lastName: "",
		nameSuffix: "",
		address1: "",
		address2: "",
		city: "",
		bankPostal:""
	});

	useEffect(() => {
	if (request) {
		setFormData((prevData) => ({
			...prevData,
			supplierName: request.vendorRecord.name,
		}));
		setIsLoading(false);
	}
}, [request]);

	useEffect(() => {
		const fetchStates = async () => {
			try {
				const response = await ServiceCalls.getVendorRegions("USA");
				if (response) {
					response.sort((a, b) => (a.region > b.region ? 1 : a.region === b.region ? 0 : -1));
					setStateList(response);
					console.log("Fetched states:", response);
				} else {
					console.error("Failed to fetch states: No response");
				}
			} catch (error) {
				console.error("Failed to fetch states:", error);
			}
		};

		fetchStates();
	}, []);

	const handleClose = () => {
		setIsOpen(false);
	};

	const handleEmptyFormFields = (data) => {
		return Object.fromEntries(
			Object.entries(data).map(([key, value]) => [key, value === "" ? null : value])
		);
	};
	const handleEmptyFields = (data) => {
        return data === undefined || data === null || data === "" ? null : data;
	};

	const handleInputChange = (e) => {
		const { name, value } = e.target;
		setFormData((prevData) => ({ ...prevData, [name]: value }));

		let errorMessage = "";
		const supplierNameRegex = /^[^<>\"/]*$/; //US8813870 Disallow <, >, ", ', / and allow '&' | DE1126615 allow '
		const validCharacterRegex = /^[a-zA-Z0-9\s.,'-]*$/;

		if (name === "supplierName") {
			if (value.trim() !== "" && !supplierNameRegex.test(value)) {
				errorMessage = "Supplier Name contains invalid characters.";
			}
		} else if (name === "firstName" || name === "lastName") {
			if (!isBusiness && value.trim() === "") {
				errorMessage = `${name === "firstName" ? "First Name" : "Last Name"} is required.`;
			} else if (!validCharacterRegex.test(value)) {
				errorMessage = `${name === "firstName" ? "First Name" : "Last Name"} contains invalid characters.`;
			}
		} else if (name === "middleName") {
			if (value.trim() !== "" && !validCharacterRegex.test(value)) {
				errorMessage = "Middle Name contains invalid characters.";
			}
		} else if (name === "nameSuffix") {
			if (value.trim() !== "" && !validCharacterRegex.test(value)) {
				errorMessage = "Name Suffix contains invalid characters.";
			}
		} else if (name === "address1" || name === "address2") {
			if (value.trim() !== "" && !validCharacterRegex.test(value)) {
				errorMessage = `${name === "address1" ? "Address 1" : "Address 2"} contains invalid characters.`;
			}
		} else if (name === "city") {
			 if (!validCharacterRegex.test(value)) {
				errorMessage = "City contains invalid characters.";
			}
		} else if (name === "bankPostal") {
			if (value.trim() !== "" && !/^\d{5}(-\d{4})?$/.test(value)) {
				errorMessage = "Invalid Postal Code.";
			}
		}

		setValidationErrors((prevErrors) => ({
			...prevErrors,
			[name]: errorMessage,
		}));
	};

	const handleSubmit = async () => {

		Object.keys(formData).forEach((field) => {
			handleInputChange({ target: { name: field, value: formData[field] } });
		});

		const newValidationErrors = { ...validationErrors };
		if (!isBusiness) {
			if (formData.firstName.trim() === "") {
				newValidationErrors.firstName = "First Name is required.";
			}
			if (formData.lastName.trim() === "") {
				newValidationErrors.lastName = "Last Name is required.";
			}
		}
		setValidationErrors(newValidationErrors);

		const hasErrors = Object.values(newValidationErrors).some((error) => error !== "");
		if (hasErrors) {
			return;
		}

		const updatedFormData = {
			...formData,
			supplierName: formData.supplierName || request.vendorRecord.name,
		};

		setIsSubmitting(true);
		const sanitizedFormData = handleEmptyFormFields(updatedFormData);

		const earlyWarningRequest = {
			vendorRecordId: request.vendorRecord.vendorRecordId,
			vendorBankRequestId: request.bankRecord.vendorBankRequestId,
			earlyWarningRequestType: isBusiness ? 1 : 2,
			isSuccessful: false,
			errorMessage: null,

			earlyWarningRequest: {
				early_warning_secondary_id: null,
				client_reference: null,
				transaction_channel: null,
				transaction_reason: null,
				bankaccount: {
					routingnumber: request.bankRecord.dfiNumber,
					accountnumber: request.bankRecord.bankAccountNumber,
				},
				business: isBusiness
					? {
						name: sanitizedFormData.supplierName,
						taxidnumber: request.vendorRecord.taxIdNumber,
					}
					: {},
				individual: !isBusiness
					? {
						firstname: sanitizedFormData.firstName,
						middlename: sanitizedFormData.middleName,
						lastname: sanitizedFormData.lastName,
						namesuffix: sanitizedFormData.nameSuffix,
						taxidnumber: request.vendorRecord.taxIdNumber,
					}
					: {},
				address: isBusiness
					? {
						addressline1: sanitizedFormData.address1,
						addressline2: sanitizedFormData.address2,
						city: sanitizedFormData.city,
						state: handleEmptyFields(bankState),
						zip: sanitizedFormData.bankPostal,
					}
					: {},
				
			},
		};

		//console.log("Form Data:", formData);
		//console.log("earlywarningrequest:", earlyWarningRequest);
		//console.log("Submitting Early Warning Request:", JSON.stringify(earlyWarningRequest, null, 2));

		try {
			const response = await ServiceCalls.resubmitEarlyWarningScore(earlyWarningRequest);
			if (response && response.isSuccessful) {
				earlyWarningRequest.earlyWarningRequest_Type = isBusiness ? 1 : 2;
				earlyWarningRequest.isSuccessful = true;
		        //console.log("Submission successful:", response);
				setSubmissionMessage("Submission successful!");
				setSubmissionMessageType("success");
			} else {
				//console.error("Submission failed:", response);
				setSubmissionMessage("Submission failed. Please try again.");
				setSubmissionMessageType("danger");
			}
			
		} catch (error) {
			//console.error("Failed to submit early warning request:", error);
			setSubmissionMessage("An error occurred. Please try again.");
			setSubmissionMessageType("danger");
		} finally {
			setIsSubmitting(false);
			setIsUpdateComplete(true);
		}
	};

	const handleRadioChange = (event) => {
		setIsBusiness(event.target.value === "business");
		setFormData({
			supplierName: request.vendorRecord.name,
			firstName: "",
			middleName: "", 
			lastName: "",
			nameSuffix: "",
			address1: "",
			address2: "",
			city: "",
			bankPostal: ""
		});
		setBankState("");
		//setBankPostal("");
		setValidationErrors({});
	};
	
	const renderBusinessColumn = () => (
		<Col>
			<Col>
				<Row style={{ marginTop: "4px" }}>
					<Col className="modal-subheader" style={{ fontSize: "1.5em" }}>Business</Col>
				</Row>
				<Row style={{ marginTop: "4px" }}>
					<Col className="modal-subheader">Supplier Name</Col>
				</Row>
				<Row style={{ marginTop: "4px" }}>
					<Col>
						<Form.Control
							type="text"
							name="supplierName"
							value={formData.supplierName}
							onChange={handleInputChange}
							maxLength="40"
							isInvalid={!!validationErrors.supplierName}
							
						/>
						<Form.Control.Feedback type="invalid">
							{validationErrors.supplierName}
						</Form.Control.Feedback>
					</Col>
				</Row>
				<Row style={{ marginTop: "4px" }}>
					<Col className="modal-subheader">Address</Col>
				</Row>
				<Row style={{ marginTop: "4px" }}>
					<Col>
						<Form.Control
							type="text"
							name="address1"
							value={formData.address1}
							onChange={handleInputChange}					
							placeholder="Address 1"
							maxLength="40"
							isInvalid={!!validationErrors.address1}
						/>
						<Form.Control.Feedback type="invalid">
							{validationErrors.address1}
						</Form.Control.Feedback>
					</Col>
				</Row>
				<Row style={{ marginTop: "4px" }}>
					<Col className="modal-subheader">Address 2 </Col>
				</Row>
				<Row style={{ marginTop: "4px" }}>
					<Col>
						<Form.Control
							type="text"
							name="address2"
							value={formData.address2}
							onChange={handleInputChange}
							placeholder="Address 2"
							maxLength="40"
							isInvalid={!!validationErrors.address2}
						/>
						<Form.Control.Feedback type="invalid">
							{validationErrors.address2}
						</Form.Control.Feedback>
					</Col>
					
				</Row>
				<Row style={{ marginTop: "4px" }}>
					<Col className="modal-subheader">City</Col>
				</Row>
				<Row style={{ marginTop: "4px" }}>
					<Col>
						<Form.Control
							type="text"
							name="city"
							value={formData.city}
							onChange={handleInputChange}
							placeholder="City"
							maxLength="25"
							isInvalid={!!validationErrors.city}
						/>
						<Form.Control.Feedback type="invalid">
							{validationErrors.city}
						</Form.Control.Feedback>
					</Col>
				</Row>
				<Row style={{ marginTop: "4px" }}>
					<Form.Group as={Col} sm={6} controlId="bankState">
						<Form.Label>State</Form.Label>
						<Form.Select
							value={bankState}
							onChange={(e) => setBankState(e.target.value)}
						>
							<option value="">State</option>
							{stateList.map((state) => (
								<option key={state.region} value={state.region}>
									{state.region}
								</option>
							))}
						</Form.Select>
					</Form.Group>
					<Form.Group as={Col} sm={6} controlId="bankPostalCode">
						<Form.Label>Postal Code</Form.Label>
						<Form.Control
							type="text"
							name="bankPostal"
							value={formData.bankPostal}
							onChange={handleInputChange} 
							placeholder="Postal Code"
							maxLength="10"
							isInvalid={!!validationErrors.bankPostal}
						/>
						<Form.Control.Feedback type="invalid">
							{validationErrors.bankPostal}
						</Form.Control.Feedback>
					</Form.Group>
				</Row>
			</Col>
		</Col>
	);

	const renderIndividualColumn = () => (
		<Col>
			<Col>
				<Row style={{ marginTop: "4px" }}>
					<Col className="modal-subheader" style={{ fontSize: "1.5em" }}>Individual</Col>
				</Row>
				<Row style={{ marginTop: "4px" }}>
					<Col className="modal-subheader">Last Name</Col>
				</Row>
				<Row style={{ marginTop: "4px" }}>
					<Col>
						<Form.Control
							type="text"
							name="lastName"
							value={formData.lastName}
							onChange={handleInputChange}
							isInvalid={!!validationErrors.lastName}
							maxLength="40"
						/>
						<Form.Control.Feedback type="invalid">
							{validationErrors.lastName}
						</Form.Control.Feedback>
					</Col>
				</Row>
				<Row style={{ marginTop: "4px" }}>
					<Col className="modal-subheader">First Name</Col>
				</Row>
				<Row style={{ marginTop: "4px" }}>
					<Col>
						<Form.Control
							type="text"
							name="firstName"
							value={formData.firstName}
							onChange={handleInputChange}
							isInvalid={!!validationErrors.firstName}
							maxLength="40"
						/>
						<Form.Control.Feedback type="invalid">
							{validationErrors.firstName}
						</Form.Control.Feedback>
					</Col>
				</Row>
				<Row style={{ marginTop: "4px" }}>
					<Col className="modal-subheader">Middle Name</Col>
				</Row>
				<Row style={{ marginTop: "4px" }}>
					<Col>
						<Form.Control
							type="text"
							name="middleName"
							value={formData.middleName}
							onChange={handleInputChange}
							maxLength="40"
							isInvalid={!!validationErrors.middleName}
						/>
						<Form.Control.Feedback type="invalid">
							{validationErrors.middleName}
						</Form.Control.Feedback>
					</Col>
				</Row>
				<Row style={{ marginTop: "4px" }}>
					<Col className="modal-subheader">Name Suffix</Col>
				</Row>
				<Row style={{ marginTop: "4px" }}>
					<Col>
						<Form.Control
							type="text"
							name="nameSuffix"
							value={formData.nameSuffix}
							onChange={handleInputChange}
							maxLength="4"
							isInvalid={!!validationErrors.nameSuffix}
						/>
						<Form.Control.Feedback type="invalid">
							{validationErrors.nameSuffix}
						</Form.Control.Feedback>
					</Col>
				</Row>
			</Col>
		</Col>
	);
	const renderModalBody = () => {
		return (
			<ModalBody style={{ minHeight: "400px", minWidth: "600px" }}>
				<div className="early-warning-content">
					<Row style={{ marginBottom: "10px" }}>
						<Col>
							<Row style={{ marginBottom: "20px", marginTop: "26px" }}>
								<Col className="modal-subheader">Supplier Tax ID</Col>
								<Col className="early-warning-resubmit-data">{RequestUtils.maskTINString(request.vendorRecord.taxIdNumber)}</Col>
							</Row>
							<Row style={{ marginBottom: "20px", marginTop: "4px" }}>
								<Col className="modal-subheader">Supplier Name</Col>
								<Col className="early-warning-resubmit-data">{request.vendorRecord.name}</Col>
							</Row>
							<Row style={{ marginBottom: "20px", marginTop: "4px" }}>
								<Col className="modal-subheader">Account Number</Col>
								<Col className="early-warning-resubmit-data">{request.bankRecord.bankAccountNumber}</Col>
							</Row>
							<Row style={{ marginBottom: "20px", marginTop: "4px" }}>
								<Col className="modal-subheader">Routing Number</Col>
								<Col className="early-warning-resubmit-data">{request.bankRecord.dfiNumber}</Col>
							</Row>
							<Row style={{ marginBottom: "20px", marginTop: "4px" }}>
							
								<Col className="">
									<Form.Check
										type="radio"
										id="account-type-business"
										label="Business"
										value="business"
										checked={isBusiness}
										onChange={handleRadioChange}
									/>
								</Col>
								<Col className="early-warning-resubmit-data">
									<Form.Check
										type="radio"
										id="account-type-individual"
										label="Individual"
										value="individual"
										checked={!isBusiness}
										onChange={handleRadioChange}
									/>
								</Col>
							</Row>
						</Col>
						{isBusiness ? renderBusinessColumn() : renderIndividualColumn() }

					</Row>
				</div>
			</ModalBody>
		);
	};


	return isLoading ? (
		<div>Loading...</div>
	) : (
			<Draggable handle=".handle">
				<Modal centered={true} size="xl" isOpen={true} toggle={handleClose} fade={false}>
					<ModalHeader toggle={handleClose} className="handle early-warning-resubmit-header" close={<></>}>
						Resubmit Early Warning
					</ModalHeader>
					{isSubmitting ? (
						<ModalBody>
							<Loading loadingText="Submitting Early Warning Request..." />
						</ModalBody>

					) : isUpdateComplete ? (
							<ModalBody>
								<TimedDisplay
									delay={3000} 
									callback={async () => {
										if (onRequestUpdate) {
											await onRequestUpdate(); 
										}
										setIsOpen(false); 
									}}
								>
									<Alert variant={submissionMessageType}>{submissionMessage}</Alert>
								</TimedDisplay>
							</ModalBody>
					) : (
							<>
							{renderModalBody()}
							<ModalFooter>
								<Button className="resubmit-early-warning-buttons" onClick={handleClose}>
									Cancel
								</Button>
								<Button className="resubmit-early-warning-buttons" onClick={handleSubmit}>
									Submit
								</Button>
							</ModalFooter>
						</>
					)}
				</Modal>
			</Draggable>
	);
};

export default ResubmitEarlyWarningPopup;